/*
 * Copyright 2026 wiz≡r Inc.
 */

import i18n.LocaleManager
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

class TeamPage(
    private val onBackClick: () -> Unit,
    private val onNavigate: ((String) -> Unit)? = null,
    private val isLoggedIn: Boolean = false,
    private val onAppNavigate: ((String) -> Unit)? = null,
) {
    private val isFrench = LocaleManager.getCurrentLocale().code == "fr"
    private val isMobile = ResponsiveUtils.isMobile()

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.innerHTML = buildHtml()

        window.setTimeout({
            if (!isMobile && onNavigate != null) {
                MarketingMenu.attachEventListeners(
                    onNavigate = onNavigate,
                    isLoggedIn = isLoggedIn,
                    onAppNavigate = onAppNavigate,
                )
            } else {
                container.querySelector("#team-back")?.addEventListener("click", {
                    onBackClick()
                })
            }

            onNavigate?.let { MarketingFooter.attachEventListeners(it) }

            if (!isMobile) {
                setupBarsAnimation()
                setupPhotoParallax()
            }
        }, 100)

        return container
    }

    private fun setupBarsAnimation() {
        val barsContainer = document.getElementById("bars-container") as? HTMLElement ?: return
        val bars3 = document.getElementById("bars-3") as? HTMLElement ?: return
        val barsFrame = document.getElementById("bars-frame") as? HTMLElement ?: return
        val barsWizer = document.getElementById("bars-wizer") as? HTMLElement ?: return

        val startScale = 0.35
        val endScale = 1.0
        val startTop = 10.0
        val endTop = window.innerHeight / 2.0 - 225

        barsContainer.style.top = "${startTop.toInt()}px"
        barsContainer.style.transform = "scale($startScale)"
        barsContainer.style.transformOrigin = "left top"

        bars3.style.opacity = "0"
        barsFrame.style.opacity = "1"
        barsWizer.style.opacity = "1"

        window.addEventListener("scroll", {
            val scrollY = window.scrollY
            val growScroll = 600.0
            val progress = (scrollY / growScroll).coerceIn(0.0, 1.0)

            val scale = startScale + (endScale - startScale) * progress
            val currentTop = startTop + (endTop - startTop) * progress
            barsContainer.style.transform = "scale($scale)"
            barsContainer.style.top = "${currentTop.toInt()}px"
            barsContainer.style.transformOrigin = "left top"

            barsWizer.style.opacity = "${(1.0 - progress * 1.4).coerceIn(0.0, 1.0)}"
            barsFrame.style.opacity = "${(1.0 - progress * 1.2).coerceIn(0.0, 1.0)}"
            bars3.style.opacity = "${((progress - 0.2) * 1.5).coerceIn(0.0, 1.0)}"

            if (scrollY > growScroll) {
                val extraScroll = scrollY - growScroll
                val drift = extraScroll * 0.15
                val maxTop = window.innerHeight - 450.0 - 50.0
                val cappedTop = (endTop + drift).coerceAtMost(maxTop)
                barsContainer.style.top = "${cappedTop.toInt()}px"
            }
        })
    }

    private fun setupPhotoParallax() {
        val ericCard = document.getElementById("eric-card") as? HTMLElement ?: return
        val ludoCard = document.getElementById("ludo-card") as? HTMLElement ?: return
        val photosSection = document.getElementById("photos-section") as? HTMLElement ?: return

        // Capture the scroll position when photos first become visible
        // to use as the baseline — prevents jump on first scroll
        var baselineScroll: Double? = null

        window.addEventListener("scroll", {
            val rect = photosSection.getBoundingClientRect()

            // Only start parallax once photos enter the viewport
            if (rect.top < window.innerHeight) {
                if (baselineScroll == null) {
                    baselineScroll = window.scrollY
                }
                val scrollSinceVisible = (window.scrollY - (baselineScroll ?: 0.0)).coerceAtLeast(0.0)
                val progress = (scrollSinceVisible / 500.0).coerceIn(0.0, 1.0)

                // Left photo drifts down moderately, right photo faster → diagonal
                val ericY = progress * 80.0
                val ludoY = progress * 220.0

                ericCard.style.transform = "translateY(${ericY.toInt()}px)"
                ludoCard.style.transform = "translateY(${ludoY.toInt()}px)"
            }
        })
    }

    private fun buildHtml(): String {
        val t = WebTheme.current
        val pageBg = if (t.isDark) "transparent" else t.background
        val textMain = if (t.isDark) "#ffffff" else t.textPrimary
        val textBody = if (t.isDark) "#d4d4d4" else t.textPrimary
        val textMuted = if (t.isDark) "#888" else t.textSecondary
        val accentColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val backLinkColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val photoBorder = if (t.isDark) "#1f1f1f" else t.border
        val title = if (isFrench) "Notre équipe" else "Our Team"
        val backText = if (isFrench) "‹ Retour" else "‹ Back"
        val introTitle = if (isFrench) "L'équipe derrière wiz≡r" else "The Team Behind wiz≡r"

        val introText = if (isFrench) """
            <p style="margin-bottom: 24px;">
                wiz≡r est le fruit d'une collaboration unique entre un père et son fils — une équipe complémentaire
                qui réunit l'expérience et l'innovation.
            </p>
            <p style="margin-bottom: 24px;">
                Avec <strong style="color: $accentColor;">25 ans d'expérience en développement logiciel dans le domaine des télécommunications</strong>,
                Eric apporte une expertise approfondie en architecture de systèmes complexes, en optimisation de la performance
                et en livraison de produits fiables à grande échelle. Sa passion pour l'intelligence artificielle et les nouvelles
                technologies l'a naturellement amené à explorer comment ces outils peuvent transformer la gestion des finances personnelles.
            </p>
            <p style="margin-bottom: 24px;">
                Ludovik, avec sa <strong style="color: $accentColor;">formation académique en finance</strong> et son réseau dans le secteur bancaire,
                apporte une compréhension profonde des mécanismes financiers, des produits d'investissement et des comportements
                d'épargne. Sa perspective fraîche sur les besoins des jeunes adultes en matière de finances guide la vision du produit.
            </p>
            <p style="margin-bottom: 24px;">
                Cette combinaison de <strong style="color: $textMain;">génie logiciel, finance et intelligence artificielle</strong>
                crée un mélange unique de connaissances et d'idées — produisant une approche optimisée et innovante des finances
                personnelles que ni l'un ni l'autre n'aurait pu concevoir seul.
            </p>
            <p style="margin-bottom: 0;">
                Ensemble, ils partagent une mission : <em style="color: $accentColor;">rendre la gestion financière si simple que tout le monde peut la maîtriser.</em>
            </p>
        """ else """
            <p style="margin-bottom: 24px;">
                wiz≡r is the result of a unique collaboration between a father and his son — a complementary team
                that brings together experience and innovation.
            </p>
            <p style="margin-bottom: 24px;">
                With <strong style="color: $accentColor;">25 years of software development experience in the telecommunications industry</strong>,
                Eric brings deep expertise in complex systems architecture, performance optimization,
                and delivering reliable products at scale. His passion for artificial intelligence and emerging
                technologies led him to explore how these tools can transform personal finance management.
            </p>
            <p style="margin-bottom: 24px;">
                Ludovik, with his <strong style="color: $accentColor;">academic background in finance</strong> and network in the banking sector,
                brings a deep understanding of financial mechanisms, investment products, and savings behaviors.
                His fresh perspective on the financial needs of young adults guides the product vision.
            </p>
            <p style="margin-bottom: 24px;">
                This combination of <strong style="color: $textMain;">software engineering, finance, and artificial intelligence</strong>
                creates a unique blend of knowledge and ideas — producing an optimized, innovative approach to personal
                finance that neither could have conceived alone.
            </p>
            <p style="margin-bottom: 0;">
                Together, they share one mission: <em style="color: $accentColor;">make personal finance so simple that anyone can master it.</em>
            </p>
        """

        val ericTitle = if (isFrench) "Fondateur & Développeur principal" else "Founder & Lead Developer"
        val ludoTitle = if (isFrench) "Co-fondateur & Stratège financier" else "Co-founder & Financial Strategist"

        val ericBio = if (isFrench)
            "B.Eng., M.A.Sc. Génie électrique · 25 ans en logiciel de télécommunications"
        else
            "B.Eng., M.A.Sc. Electrical Engineering · 25 years in telecommunication software"

        val ludoBio = if (isFrench)
            "Finance · Secteur bancaire · Vision produit"
        else
            "Finance · Banking sector · Product vision"

        if (isMobile) return buildMobileHtml(title, backText, introTitle, introText, ericTitle, ludoTitle, ericBio, ludoBio)

        val ambientGlow = if (t.isDark) """
                <div style="position: fixed; top: 0; left: 0; right: 0; height: 56px; background: transparent; z-index: 0; pointer-events: none;"></div>
        """ else ""

        return """
            <div style="background: $pageBg; min-height: 100vh; color: $textBody; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; position: relative; overflow-x: hidden;">

                $ambientGlow

                ${if (onNavigate != null) {
                    MarketingMenu.hamburgerButtonHtml() + MarketingMenu.overlayHtml(currentPage = "team", isLoggedIn = isLoggedIn)
                } else {
                    """<div style="position: fixed; top: 20px; right: 24px; z-index: 100;">
                        <a id="team-back" href="#" style="color: $backLinkColor; text-decoration: none; font-size: 15px; font-weight: 500; cursor: pointer;">$backText</a>
                    </div>"""
                }}

                <!-- 3-bars animated container -->
                <div id="bars-container" style="position: fixed; left: 0; top: 10px; z-index: 1; pointer-events: none; transition: none; ${if (t.isDark) "" else "opacity: 0.3; filter: brightness(1.5);"}">
                    <img id="bars-3" src="/team-3-bars.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 0;">
                    <img id="bars-frame" src="/team-bars-wizer-frame.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                    <img id="bars-wizer" src="/team-wizer-only.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                </div>

                <!-- Content -->
                <div style="position: relative; z-index: 2; padding: 80px 48px 0; padding-right: 80px;">

                    <!-- Title & text section -->
                    <div id="text-section" style="padding-left: 440px; margin-bottom: 0; position: relative; padding-bottom: 40px;">
                        <h1 style="font-size: 44px; font-weight: 700; color: $textMain; margin-bottom: 8px;">$title</h1>
                        <h2 style="font-size: 22px; font-weight: 400; color: $textMuted; margin-bottom: 40px;">$introTitle</h2>
                        <div style="color: $textBody; line-height: 1.8; font-size: 16px;">
                            $introText
                        </div>
                    </div>

                    <!-- Team photos section -->
                    <div id="photos-section" style="display: flex; align-items: flex-start; gap: 40px; margin-top: 40px; margin-bottom: 340px; padding-left: 440px; position: relative;">
                        <!-- Eric -->
                        <div id="eric-card" style="flex: 1; text-align: center; will-change: transform;">
                            <div style="width: 240px; height: 300px; border-radius: 16px; overflow: hidden; margin: 0 auto 20px; border: 2px solid $photoBorder;">
                                <img src="/team-eric.jpeg" style="width: 100%; height: 100%; object-fit: cover; transform: scale(1.15);">
                            </div>
                            <h3 style="color: $textMain; font-size: 20px; font-weight: 600; margin: 0 0 4px;">Eric Laurin</h3>
                            <p style="color: $accentColor; font-size: 14px; font-weight: 500; margin: 0 0 8px;">$ericTitle</p>
                            <p style="color: $textMuted; font-size: 13px; margin: 0;">$ericBio</p>
                        </div>
                        <!-- Ludovik -->
                        <div id="ludo-card" style="flex: 1; text-align: center; will-change: transform;">
                            <div style="width: 240px; height: 300px; border-radius: 16px; overflow: hidden; margin: 0 auto 20px; border: 2px solid $photoBorder;">
                                <img src="/team-ludo.jpeg" style="width: 100%; height: 100%; object-fit: cover;">
                            </div>
                            <h3 style="color: $textMain; font-size: 20px; font-weight: 600; margin: 0 0 4px;">Ludovik Laurin</h3>
                            <p style="color: $accentColor; font-size: 14px; font-weight: 500; margin: 0 0 8px;">$ludoTitle</p>
                            <p style="color: $textMuted; font-size: 13px; margin: 0;">$ludoBio</p>
                        </div>
                    </div>
                </div>

                ${MarketingFooter.html(currentPage = "team")}
            </div>
        """.trimIndent()
    }

    private fun buildMobileHtml(
        title: String, backText: String, introTitle: String, introText: String,
        ericTitle: String, ludoTitle: String, ericBio: String, ludoBio: String
    ): String {
        val t = WebTheme.current
        val pageBg = if (t.isDark) "transparent" else t.background
        val textMain = if (t.isDark) "#ffffff" else t.textPrimary
        val textBody = if (t.isDark) "#d4d4d4" else t.textPrimary
        val textMuted = if (t.isDark) "#888" else t.textSecondary
        val accentColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val backLinkColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val photoBorder = if (t.isDark) "#1f1f1f" else t.border
        val mobileAmbientGlow = if (t.isDark) """
                <div style="position: fixed; top: 0; left: 0; right: 0; height: 56px; background: transparent; z-index: 0; pointer-events: none;"></div>
        """ else ""
        return """
            <div style="background: $pageBg; min-height: 100vh; color: $textBody; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; position: relative; overflow-x: hidden;">

                $mobileAmbientGlow

                <!-- Back button (top right) -->
                <div style="position: fixed; top: 20px; right: 24px; z-index: 100;">
                    <a id="team-back" href="#" style="color: $backLinkColor; text-decoration: none; font-size: 15px; font-weight: 500; cursor: pointer;">$backText</a>
                </div>

                <!-- Content -->
                <div style="position: relative; z-index: 2; max-width: 900px; margin: 0 auto; padding: 60px 20px 0;">
                    <div style="margin-bottom: 60px;">
                        <h1 style="font-size: 32px; font-weight: 700; color: $textMain; margin-bottom: 8px;">$title</h1>
                        <h2 style="font-size: 18px; font-weight: 400; color: $textMuted; margin-bottom: 40px;">$introTitle</h2>
                        <div style="color: $textBody; line-height: 1.8; font-size: 15px;">
                            $introText
                        </div>
                    </div>

                    <!-- Team photos — vertical on mobile -->
                    <div style="display: flex; flex-direction: column; align-items: center; gap: 40px; margin-top: 40px; margin-bottom: 60px;">
                        <!-- Eric -->
                        <div style="text-align: center;">
                            <div style="width: 200px; height: 250px; border-radius: 16px; overflow: hidden; margin: 0 auto 20px; border: 2px solid $photoBorder;">
                                <img src="/team-eric.jpeg" style="width: 100%; height: 100%; object-fit: cover; transform: scale(1.15);">
                            </div>
                            <h3 style="color: $textMain; font-size: 20px; font-weight: 600; margin: 0 0 4px;">Eric Laurin</h3>
                            <p style="color: $accentColor; font-size: 14px; font-weight: 500; margin: 0 0 8px;">$ericTitle</p>
                            <p style="color: $textMuted; font-size: 13px; margin: 0;">$ericBio</p>
                        </div>
                        <!-- Ludovik -->
                        <div style="text-align: center;">
                            <div style="width: 200px; height: 250px; border-radius: 16px; overflow: hidden; margin: 0 auto 20px; border: 2px solid $photoBorder;">
                                <img src="/team-ludo.jpeg" style="width: 100%; height: 100%; object-fit: cover;">
                            </div>
                            <h3 style="color: $textMain; font-size: 20px; font-weight: 600; margin: 0 0 4px;">Ludovik Laurin</h3>
                            <p style="color: $accentColor; font-size: 14px; font-weight: 500; margin: 0 0 8px;">$ludoTitle</p>
                            <p style="color: $textMuted; font-size: 13px; margin: 0;">$ludoBio</p>
                        </div>
                    </div>
                </div>

                ${MarketingFooter.html(currentPage = "team")}
            </div>
        """.trimIndent()
    }
}
